// Bloques B/E/F/G/I de 019 (Fase 1B, Tanda 16, ver PLAN.md) — cierra 019 y
// toda la Fase 1B. Un componente por punto de inserción en el JSX de
// `Formulario019.tsx`: B va después del encabezado (antes de C), E/F/G/I
// van después de H (condición de egreso).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import {
  CAMPOS_ELIMINACION,
  CAMPOS_INGRESOS,
  TECNICA_ANESTESICA_019_OPCIONES,
  TOPE_FILAS_EVOLUCION,
  filaEvolucionVacia,
  type EstadoBloqueB019,
  type EstadoBloquesRestantes019,
  type FilaEvolucion,
} from './bloquesRestantes019Datos';

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoNumero({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

interface Bloque019Props {
  readonly estado: EstadoBloquesRestantes019;
  readonly onChange: (estado: EstadoBloquesRestantes019) => void;
}

// ---------- B (después del encabezado, antes de C) ----------

interface Bloque019BProps extends Bloque019Props {
  readonly diagnostico: DiagnosticoCie10Valor;
  readonly onChangeDiagnostico: (v: DiagnosticoCie10Valor) => void;
}

export function Bloque019B({ estado, onChange, diagnostico, onChangeDiagnostico }: Bloque019BProps): JSX.Element {
  const { t } = useTranslation();

  function actualizarB(cambios: Partial<EstadoBloqueB019>): void {
    onChange({ ...estado, b: { ...estado.b, ...cambios } });
  }

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueB.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <CampoTexto id="f019-b-especialidad" label={t('formularios.f019.bloqueB.especialidad')} value={estado.b.especialidad} onChange={(v) => actualizarB({ especialidad: v })} />
        <SelectorDiagnosticoCie10 label={t('formularios.f019.bloqueB.diagnostico')} value={diagnostico} onChange={onChangeDiagnostico} />
        <CampoTexto id="f019-b-cirugia-realizada" label={t('formularios.f019.bloqueB.cirugiaRealizada')} value={estado.b.cirugiaRealizada} onChange={(v) => actualizarB({ cirugiaRealizada: v })} />
      </div>
      <div className="flex flex-col gap-2">
        <Label>{t('formularios.f019.bloqueB.tecnicaAnestesica')}</Label>
        <ChecklistGrid
          claves={TECNICA_ANESTESICA_019_OPCIONES}
          prefijoI18n="formularios.f019.bloqueB.tecnicaAnestesicaOpciones"
          prefijoId="f019-b-tecnica"
          valores={estado.b.tecnicaAnestesica}
          onChange={(v) => actualizarB({ tecnicaAnestesica: v })}
        />
      </div>
    </div>
  );
}

// ---------- E/F/G/I (después de H) ----------

function BloqueEF({ estado, onChange }: Bloque019Props): JSX.Element {
  const { t } = useTranslation();

  function actualizarIngreso(campo: (typeof CAMPOS_INGRESOS)[number], valor: string): void {
    onChange({ ...estado, ingresos: { ...estado.ingresos, [campo]: valor } });
  }
  function actualizarEliminacion(campo: (typeof CAMPOS_ELIMINACION)[number], valor: string): void {
    onChange({ ...estado, eliminacion: { ...estado.eliminacion, [campo]: valor } });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueE.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {CAMPOS_INGRESOS.map((campo) => (
            <CampoNumero key={campo} id={`f019-e-${campo}`} label={t(`formularios.f019.bloqueE.campos.${campo}`)} value={estado.ingresos[campo]} onChange={(v) => actualizarIngreso(campo, v)} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueF.titulo')}</h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          {CAMPOS_ELIMINACION.map((campo) => (
            <CampoNumero key={campo} id={`f019-f-${campo}`} label={t(`formularios.f019.bloqueF.campos.${campo}`)} value={estado.eliminacion[campo]} onChange={(v) => actualizarEliminacion(campo, v)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BloqueG({ estado, onChange }: Bloque019Props): JSX.Element {
  const { t } = useTranslation();

  function actualizarFila(id: string, campo: keyof Omit<FilaEvolucion, 'id'>, valor: string): void {
    onChange({ ...estado, evolucion: estado.evolucion.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)) });
  }

  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueG.titulo')}</h4>
      <div className="flex flex-col gap-2">
        {estado.evolucion.map((f) => (
          <div key={f.id} className="grid grid-cols-1 gap-2 rounded-md border border-border p-3 sm:grid-cols-5 sm:items-end">
            <div className="flex flex-col gap-1">
              <Label htmlFor={`f019-g-${f.id}-fecha`}>{t('formularios.f019.bloqueG.fecha')}</Label>
              <Input id={`f019-g-${f.id}-fecha`} type="date" value={f.fecha} onChange={(e) => actualizarFila(f.id, 'fecha', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`f019-g-${f.id}-hora`}>{t('formularios.f019.bloqueG.hora')}</Label>
              <Input id={`f019-g-${f.id}-hora`} placeholder="hh:mm" value={f.hora} onChange={(e) => actualizarFila(f.id, 'hora', e.target.value)} />
            </div>
            <CampoTexto id={`f019-g-${f.id}-evolucion`} label={t('formularios.f019.bloqueG.evolucion')} value={f.evolucion} onChange={(v) => actualizarFila(f.id, 'evolucion', v)} />
            <CampoTexto id={`f019-g-${f.id}-prescripcion`} label={t('formularios.f019.bloqueG.prescripcion')} value={f.prescripcion} onChange={(v) => actualizarFila(f.id, 'prescripcion', v)} />
            <div className="flex items-end gap-1">
              <CampoTexto id={`f019-g-${f.id}-farmacos`} label={t('formularios.f019.bloqueG.administracionFarmacos')} value={f.administracionFarmacos} onChange={(v) => actualizarFila(f.id, 'administracionFarmacos', v)} />
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...estado, evolucion: estado.evolucion.filter((row) => row.id !== f.id) })}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-fit" disabled={estado.evolucion.length >= TOPE_FILAS_EVOLUCION} onClick={() => onChange({ ...estado, evolucion: [...estado.evolucion, filaEvolucionVacia()] })}>
          {t('formularios.f019.bloqueG.agregarFila')}
        </Button>
      </div>
    </div>
  );
}

function BloqueI(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f019.bloqueI.titulo')}</h4>
      <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
    </div>
  );
}

export function Bloque019EFGI({ estado, onChange }: Bloque019Props): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <BloqueEF estado={estado} onChange={onChange} />
      <BloqueG estado={estado} onChange={onChange} />
      <BloqueI />
    </div>
  );
}
